import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import { Btn } from "@/ui/btn/Btn";
import { useInstagramSearch } from "../../hooks/useInstagramSearch";
import { useInstagramSearchProfiles } from "../../hooks/useInstagramSearchProfiles";
import { useInstagramSearchStorage } from "../../hooks/useInstagramSearchStorage";
import { isUsername } from "../../utils/utils";
import {
  InstagramSearchEmptyForUsername,
  InstagramSearchEmptyProfiles,
} from "../instagram-search-empty/InstagramSearchEmpty";
import { InstagramSearchHeaderProfiles } from "../instagram-search-header/InstagramSearchHeader";
import { InstagramSearchLoader } from "../instagram-search-loader/InstagramSearchLoader";
import { InstagramSearchProfile } from "../instagram-search-profile/InstagramSearchProfile";
import { InstagramSearchProfilesSelection } from "../instagram-search-selection/InstagramSearchSelection";
import styles from "./InstagramSearchProfilesContent.module.css";

export const InstagramSearchProfilesContent = () => {
  const { t } = useTranslation();
  const query = useInstagramSearch((state) => state.query);
  const layout = useInstagramSearchStorage((state) => state.layout);
  const {
    data,
    error,
    isLoading,
    isValidating,
    isPaginationAvailable,
    setSize,
    mutate,
  } = useInstagramSearchProfiles();

  if (error instanceof Error) {
    return (
      <div className={styles.content}>
        <InstagramSearchEmptyProfiles
          isLoading={isLoading || isValidating}
          refresh={mutate}
        />
      </div>
    );
  }

  const page = data?.[data.length - 1];

  if (data === undefined || page === undefined) {
    return (
      <div className={styles.content}>
        <InstagramSearchContentLoader />
      </div>
    );
  }

  const { count, totalCount } = page;

  const profiles = data.flatMap((page) => page.profiles);

  return (
    <div className={clsx(styles.content, isLoading && styles.loading)}>
      <InstagramSearchHeaderProfiles count={count} totalCount={totalCount} />

      <div className={styles.separator} />

      <InstagramSearchProfilesSelection
        profiles={profiles}
        loading={isLoading}
      />

      {profiles.length === 0 && isUsername(query) === true && (
        <InstagramSearchEmptyForUsername />
      )}

      {profiles.length === 0 && isUsername(query) === false && (
        <InstagramSearchEmptyProfiles
          isLoading={isLoading || isValidating}
          refresh={mutate}
        />
      )}

      {profiles.length > 0 && (
        <div className={clsx(styles.list, styles[layout])}>
          {profiles.map((profile) => (
            <InstagramSearchProfile key={profile.id} profile={profile} />
          ))}
        </div>
      )}

      {isPaginationAvailable && (
        <Btn
          className={styles.pagination}
          icon="arrow_down_long_24px_regular"
          text={t("pagination.showMore")}
          loading={isValidating}
          onClick={() => setSize((size) => size + 1)}
        />
      )}
    </div>
  );
};

const InstagramSearchContentLoader = () => {
  const layout = useInstagramSearchStorage((state) => state.layout);

  return (
    <>
      <InstagramSearchLoader className={styles.loader} />
      <div className={styles.separator} />
      <InstagramSearchLoader className={styles.loader} />
      <div className={clsx(styles.list, styles[layout])}>
        <InstagramSearchLoader className={styles.loader} />
        <InstagramSearchLoader className={styles.loader} />
        <InstagramSearchLoader className={styles.loader} />
        <InstagramSearchLoader className={styles.loader} />
        <InstagramSearchLoader className={styles.loader} />
        <InstagramSearchLoader className={styles.loader} />
      </div>
    </>
  );
};
